/**
 * SearchOverlay — v6 全局搜索浮层 (Smart Search Engine)
 *
 * Uses smart-search.ts engine for cross-entity relevance scoring:
 * - Weighted field matching (title > concern > content)
 * - Entity-type filtering (atom/topic/discussion)
 * - Recency boost for fresh content
 * - Search suggestions from top concerns
 *
 * @see src/lib/smart-search.ts
 * @see Issue #137 — Smart Search Upgrade
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAtoms, useTopics, type AtomRow, type TopicListItem } from "@/hooks/use-supabase";
import { ActivityIcon } from "@/components/ActivityIcon";
import {
  search as smartSearch,
  buildSuggestions,
  type SearchResult,
  type SearchEntityType,
} from "@shared-lib/smart-search";
import type { TopicRow } from "@shared-types/api-rows";

const TYPE_META: Record<string, { label: string; iconType: string; color: string; href: (id: string) => string }> = {
  topic:      { label: "话题", iconType: "list", color: "bg-blue-50 text-blue-600", href: (id) => `/topic/${id}` },
  atom:       { label: "对谈", iconType: "resonance", color: "bg-amber-50 text-amber-700", href: (id) => `/talk/${id}` },
  discussion: { label: "讨论", iconType: "discuss", color: "bg-emerald-50 text-emerald-600", href: (id) => `/topic/${id}` },
};

const FILTER_OPTIONS: { key: SearchEntityType | "all"; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "atom", label: "对谈" },
  { key: "topic", label: "话题" },
  { key: "discussion", label: "讨论" },
];

interface SearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export function SearchOverlay({ open, onClose }: SearchOverlayProps) {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [typeFilter, setTypeFilter] = useState<SearchEntityType | "all">("all");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // #555: Load search corpus only when user has typed something (defer DB queries)
  const hasQuery = debouncedQuery.trim().length > 0;
  const { data: atomsData } = useAtoms({ limit: 200, skip: !hasQuery });
  const { data: topicsData } = useTopics({ limit: 100, skip: !hasQuery });
  const atoms = (atomsData?.atoms ?? []) as AtomRow[];
  const topics = (topicsData?.topics ?? []) as TopicListItem[];

  // Debounce
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 150);
    return () => clearTimeout(timer);
  }, [query]);

  // Smart search via engine
  const searchResponse = useMemo(() => {
    if (!debouncedQuery.trim()) return null;
    return smartSearch(
      {
        text: debouncedQuery,
        entityTypes: typeFilter === "all" ? undefined : [typeFilter],
        limit: 20,
        recencyBoostDays: 14,
      },
      atoms,
      topics as unknown as TopicRow[],
      [], // discussions — will be populated when real data is connected
    );
  }, [debouncedQuery, atoms, topics, typeFilter]);

  const results: SearchResult[] = searchResponse?.results ?? [];
  const suggestions: string[] = useMemo(() => {
    if (debouncedQuery.length >= 1 && debouncedQuery.length <= 3) {
      return buildSuggestions(debouncedQuery, atoms, topics as unknown as TopicRow[], 5);
    }
    return searchResponse?.suggestions ?? [];
  }, [debouncedQuery, atoms, topics, searchResponse]);

  /* Focus input on open */
  useEffect(() => {
    if (open) {
      setQuery("");
      setDebouncedQuery("");
      setSelectedIdx(0);
      setTypeFilter("all");
      // #546: Use rAF for focus instead of setTimeout
      const raf = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(raf);
    }
  }, [open]);

  /* Global Escape key listener (#440) — close overlay from any focus context */
  useEffect(() => {
    if (!open) return;
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); }
    };
    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, [open, onClose]);

  /* Reset selection when results change */
  useEffect(() => {
    setSelectedIdx(0);
  }, [results.length]);

  /* Keyboard navigation */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") { onClose(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); return; }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); return; }
    if (e.key === "Enter" && results[selectedIdx]) {
      const r = results[selectedIdx];
      const meta = TYPE_META[r.type];
      if (meta) {
        onClose();
        navigate(meta.href(r.type === "discussion" ? (r.metadata.topic_id as string ?? r.id) : r.id));
      }
    }
  }, [results, selectedIdx, onClose, navigate]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[var(--z-dialog)] flex items-start justify-center pt-[10vh]" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" />
      {/* Panel */}
      <div
        className="relative w-full max-w-lg bg-[var(--ig-surface)] rounded-xl shadow-2xl border border-slate-200/80 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <svg className="w-5 h-5 text-slate-400 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="搜索话题、对谈、讨论..."
            aria-label="搜索"
            className="flex-1 text-sm text-slate-800 placeholder:text-slate-400 outline-none bg-transparent"
          />
          {searchResponse && (
            <span className="text-xs text-slate-400 shrink-0 tabular-nums">
              {searchResponse.totalCount} 结果 · {searchResponse.timing_ms}ms
            </span>
          )}
          <kbd className="hidden sm:inline-block text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">ESC</kbd>
        </div>

        {/* Type filters */}
        {debouncedQuery && (
          <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-50">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setTypeFilter(opt.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                  typeFilter === opt.key
                    ? "bg-blue-100 text-blue-700"
                    : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {/* Suggestions */}
          {suggestions.length > 0 && results.length === 0 && (
            <div className="px-4 py-3 border-b border-slate-50">
              <p className="text-xs text-slate-400 mb-2">建议搜索</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => { setQuery(s); setDebouncedQuery(s); }}
                    className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 text-xs hover:bg-slate-200 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {debouncedQuery && results.length === 0 && suggestions.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              未找到匹配结果
            </div>
          )}
          {results.map((r, idx) => {
            const meta = TYPE_META[r.type];
            if (!meta) return null;
            const href = meta.href(r.type === "discussion" ? (r.metadata.topic_id as string ?? r.id) : r.id);
            return (
              <Link
                key={`${r.type}-${r.id}`}
                to={href}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  idx === selectedIdx ? "bg-blue-50" : "hover:bg-slate-50"
                }`}
              >
                <ActivityIcon type={meta.iconType} className="w-4 h-4" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate">{r.title}</div>
                  <div className="text-xs text-slate-400 truncate">{r.snippet}</div>
                  {r.matchedFields.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {r.matchedFields.map((f) => (
                        <span key={f} className="text-xs text-slate-300">{f}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${meta.color}`}>
                    {meta.label}
                  </span>
                  <div className="text-xs text-slate-300 mt-1 tabular-nums">
                    {r.score.toFixed(1)}
                  </div>
                </div>
              </Link>
            );
          })}
          {!debouncedQuery && (
            <div className="px-4 py-6 text-center text-xs text-slate-400">
              输入关键词搜索话题、对谈或讨论
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
